import { createContext, FunctionComponent } from "preact";
import { useCallback, useContext, useEffect, useRef, useState, MutableRef } from "preact/hooks";
import { useConfig } from "./ConfigProvider";
import { useWidgetCookie } from "../hooks/widgetCookie";
import { useSendUrlUtmData } from "../hooks/sendUrlUtmData";

export type WidgetMode = 'menu' | 'chat' | 'voice';

type UnifiedWidgetState = {
  open: boolean;
  mode: WidgetMode;
  // Voice-specific state
  conversationId: string | null;
  isMinimized: boolean;
  error: string | null;
}

type UnifiedWidgetStateContext = UnifiedWidgetState & {
  setState: (state: UnifiedWidgetState) => void;
  updateState: (state: Partial<UnifiedWidgetState>) => void;
  setMode: (mode: WidgetMode) => void;
  toggleWidget: () => void;
  closeWidget: () => void;
  initalised: boolean;
  widgetIframe: MutableRef<HTMLIFrameElement | null>;
  setConversationId: (id: string | null) => void;
  setIsMinimized: (minimized: boolean) => void;
  setError: (error: string | null) => void;
  cookieInitialised: boolean;
}

const UnifiedWidgetStateContext = createContext<UnifiedWidgetStateContext | undefined>(undefined);

/*
 * The unified widget provider manages the state for both chat and voice modes.
 * It orchestrates mode switching and ensures proper initialization.
 */
export const UnifiedWidgetProvider: FunctionComponent = ({ children }) => {
  const { updateConfig, config } = useConfig();
  const { initialised: cookieInitialised } = useWidgetCookie();
  const widgetIframe = useRef<HTMLIFrameElement | null>(null);
  useSendUrlUtmData(widgetIframe);

  const { autoOpen } = config;
  const [state, setState] = useState<UnifiedWidgetState>({
    open: false, // Never auto-open, let user click button
    mode: 'menu', // Always start in menu mode
    conversationId: null,
    isMinimized: false,
    error: null,
  });

  // Always show the widget button - cookie init only needed for chat mode
  const initalised = true;

  const updateState = useCallback((newState: Partial<UnifiedWidgetState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  }, [setState]);

  const setMode = useCallback((mode: WidgetMode) => {
    updateState({ mode, open: true });
  }, [updateState]);

  // Determine the default mode based on chatOptions config
  const getDefaultMode = useCallback((): WidgetMode => {
    if (config.chatOptions === 'chat') return 'chat';
    if (config.chatOptions === 'voice') return 'voice';
    if (config.chatOptions === 'chat_and_voice') return 'menu';
    return 'menu'; // default to menu
  }, [config.chatOptions]);

  const toggleWidget = useCallback(() => {
    setState((prevState) => {
      const opening = !prevState.open;
      return {
        ...prevState,
        open: opening,
        // When opening, go to default mode based on chatOptions
        mode: opening ? getDefaultMode() : prevState.mode,
      };
    });
  }, [getDefaultMode]);

  const closeWidget = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      open: false,
      mode: getDefaultMode(),
    }));
  }, [getDefaultMode]);

  const setConversationId = useCallback((id: string | null) => {
    updateState({ conversationId: id });
  }, [updateState]);

  const setIsMinimized = useCallback((minimized: boolean) => {
    updateState({ isMinimized: minimized });
  }, [updateState]);

  const setError = useCallback((error: string | null) => {
    updateState({ error });
  }, [updateState]);

  useEffect(() => {
    // Create a UnifiedWidget object for interfacing with the widget
    const existingWidget = window.UnifiedWidget || { init: () => {} };
    window.UnifiedWidget = { ...existingWidget, setConfig: updateConfig };
  }, []);

  return (
    <UnifiedWidgetStateContext.Provider value={{
      ...state,
      setState,
      updateState,
      setMode,
      toggleWidget,
      closeWidget,
      initalised,
      widgetIframe,
      setConversationId,
      setIsMinimized,
      setError,
      cookieInitialised, // Pass this separately for chat mode to check
    }}>
      {children}
    </UnifiedWidgetStateContext.Provider>
  )
}

export const useUnifiedWidget = (): UnifiedWidgetStateContext => {
  const context = useContext(UnifiedWidgetStateContext);
  if (!context) {
    throw new Error('useUnifiedWidget must be used within a UnifiedWidgetProvider');
  }
  return context;
};

